import React from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstanceShoppaBackend } from '../integrations/axios.config';
import { getSessionInfo } from '../utils/getSessionInfo';
import { saveSession } from '../utils/storage';
import { getEnvs } from '../utils/getEnvs';
import { Tracking } from '../analytics/tracking';
import { captureException } from '../integrations/sentry/capture';
import { createPinCodeStatus, updatePinCodeStatus } from '../utils/pincode';

type Location = {
	_id: string;
	alias: string;
	address: string;
	city: string;
	state: string;
	defaultTaxId?: string | null;
};

const SelectLocation = () => {
	const [locations, setLocations] = React.useState<Location[]>([]);
	const [isLoading, setIsLoading] = React.useState(true);
	const [error, setError] = React.useState<string | null>(null);
	const [selectedId, setSelectedId] = React.useState<string | null>(null);

	const navigate = useNavigate();
	const { locationId } = getSessionInfo();

	React.useEffect(() => {
		if (locationId) {
			navigate('/terminal');
			return;
		}

		const fetchLocations = async () => {
			try {
				const res = (await axiosInstanceShoppaBackend.get('/user/locations?pageSize=100')) as unknown as {
					data: { locations: Location[] };
				};
				const data = res?.data?.locations ?? [];
				setLocations(data);
			} catch (err: unknown) {
				captureException(err, { fn: 'SelectLocation.fetchLocations' });
				const message = err instanceof Error ? err.message : 'Failed to load locations';
				setError(message);
			} finally {
				setIsLoading(false);
			}
		};

		fetchLocations();
	}, [locationId, navigate]);

	const handleSelect = async (location: Location) => {
		setSelectedId(location._id);

		const { user } = getEnvs();
		const currentUserInfo = getSessionInfo().userInfo;

		if (!currentUserInfo) return;

		const updatedUserInfo = {
			...currentUserInfo,
			assignedLocations: [
				{
					id: location._id,
					city: location.city,
					state: location.state,
					address: location.address,
					defaultTaxId: location.defaultTaxId ?? null,
				},
			],
		};

		saveSession(user, updatedUserInfo);

		// Initialize pincode status (authService skips this for owners)
		await createPinCodeStatus(false);
		await updatePinCodeStatus(currentUserInfo.flags?.passcodeEnabled ?? false);

		Tracking.capture('owner_location_selected', {
			userId: currentUserInfo._id,
			locationId: location._id,
			locationAlias: location.alias,
		});

		const isPasscodeEnabled = currentUserInfo.flags?.passcodeEnabled;

		if (!isPasscodeEnabled) {
			navigate('/passcode');
		} else {
			navigate('/terminal');
		}

		window.location.reload();
	};

	if (isLoading) {
		return (
			<section className="container-fluid">
				<div className="row justify-content-center align-items-center overflow-hidden" style={{ height: '100vh' }}>
					<div className="col-12 col-sm-8 col-md-7 col-lg-6 col-xl-4 col-xxl-4 text-center">
						<div className="spinner-border" role="status">
							<span className="visually-hidden">Loading...</span>
						</div>
						<p className="mt-3 text-muted">Loading locations...</p>
					</div>
				</div>
			</section>
		);
	}

	if (error) {
		return (
			<section className="container-fluid">
				<div className="row justify-content-center align-items-center overflow-hidden" style={{ height: '100vh' }}>
					<div className="col-12 col-sm-8 col-md-7 col-lg-6 col-xl-4 col-xxl-4 text-center">
						<p className="text-danger mb-3">{error}</p>
						<button className="btn btn-outline-dark" onClick={() => window.location.reload()}>
							Try Again
						</button>
					</div>
				</div>
			</section>
		);
	}

	if (locations.length === 0) {
		return (
			<section className="container-fluid">
				<div className="row justify-content-center align-items-center overflow-hidden" style={{ height: '100vh' }}>
					<div className="col-12 col-sm-8 col-md-7 col-lg-6 col-xl-4 col-xxl-4 text-center">
						<h5 className="mb-3">No Locations Found</h5>
						<p className="text-muted">No locations found. Create a location in the BackOffice first.</p>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="container-fluid">
			<div className="row justify-content-center align-items-center overflow-hidden" style={{ minHeight: '100vh' }}>
				<div className="col-12 col-sm-8 col-md-7 col-lg-6 col-xl-5 col-xxl-4">
					<h4 className="text-center mb-2">Select a Location</h4>
					<p className="text-center text-muted mb-4">Choose the location you want to operate from</p>

					<div className="d-flex flex-column gap-3">
						{locations.map((location) => (
							<button
								key={location._id}
								className={`card p-3 border text-start w-100 ${
									selectedId === location._id ? 'border-dark' : 'border-light'
								}`}
								style={{
									cursor: selectedId ? 'not-allowed' : 'pointer',
									background: selectedId === location._id ? '#f8f9fa' : '#fff',
									transition: 'all 0.15s ease',
								}}
								disabled={selectedId !== null}
								onClick={() => handleSelect(location)}
							>
								<h6 className="mb-1">{location.alias}</h6>
								<small className="text-muted">
									{[location.address, location.city, location.state].filter(Boolean).join(', ')}
								</small>
							</button>
						))}
					</div>
				</div>
			</div>
		</section>
	);
};

export default SelectLocation;
